import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import moment from 'moment';
import DayPickerInput from 'react-day-picker/DayPickerInput';
import DropList from 'epic-diesel-form/src/components/common/DropList';
import {Icon, Message, Button} from 'epic-diesel-common';
import {sizes, svgs, priority, buttonTypes} from 'epic-diesel-common/src/constants';
import {getMessage} from '../../common/Message';
import Tip from '../../common/Tip';
import Card from './Card';
import styles from './GraphsSection.pcss';

const COLORS = ['#572289', '#DF6E26', '#B72C63', '#387CEA', '#0DAD1D', '#DBE82C'];

export default class GraphsSection extends React.Component {
    static propTypes = {
        messages: PropTypes.object,
        className: PropTypes.string,
        sectionData: PropTypes.object,
        enableLink: PropTypes.bool,
        showLink: PropTypes.bool,
        loading: PropTypes.bool,
        onChange: PropTypes.func
    };

    static defaultProps = {
        sectionData: {}
    };

    constructor(props) {
        super(props);
        this.state = {
            time: 'month',
            showEarnings: true,
            showMoreTitles: true,
            sortMoreTitles: 'name',
            sortAsc: true,
            searchEarnings: '',
            searchMoreTitles: '',
            from: moment()
                .add(-1, 'months')
                .format('YYYY-MM-DD'),
            to: moment().format('YYYY-MM-DD')
        };
    }

    onSubmit = () => {
        const {from, to} = this.state;
        this.props.onChange({from, to});
    };

    onChange = name => value => {
        // If we change the "from", restrict the "to"
        const newState = {[name]: value};

        if (name === 'from') {
            const {to} = this.state;

            const startDate = new Date(value);
            const currentEndDate = new Date(to);

            if (startDate > currentEndDate) {
                /*eslint new-cap: ["error", { "newIsCap": false }]*/
                newState.to = new moment(startDate).add(1, 'months').format('YYYY-MM-DD');
            }

            const endDate = new Date(startDate);
            endDate.setMonth(endDate.getMonth() + 2);

            if (endDate < currentEndDate) {
                endDate.setMonth(endDate.getMonth() - 1);
                /*eslint new-cap: ["error", { "newIsCap": false }]*/
                newState.to = new moment(endDate).format('YYYY-MM-DD');
            }
        }

        this.setState(newState);
    };

    onSearch = event => {
        const {name, value} = event.target;
        if (value && value.length) {
            event.target.classList.add(styles.hasData);
        } else {
            event.target.classList.remove(styles.hasData);
        }
        this.setState({[name]: value});
    };

    onSort = (name, value) => {
        this.setState({
            [name]: value,
            sortAsc: this.state[name] === value ? !this.state.sortAsc : true
        });
    };

    toggleEarnings = () => {
        this.setState({showEarnings: !this.state.showEarnings});
    };

    toggleMoreTitles = () => {
        this.setState({showMoreTitles: !this.state.showMoreTitles});
    };

    render() {
        const {
            time,
            showEarnings,
            showMoreTitles,
            sortMoreTitles,
            sortAsc,
            from,
            to,
            searchEarnings,
            searchMoreTitles
        } = this.state;
        const {className, sectionData, enableLink, showLink, messages, loading} = this.props;
        const sortOptions = [
            {
                label: getMessage(messages, 'epic.affiliate.earnings.sort_name'),
                value: 'name'
            },
            {
                label: getMessage(messages, 'epic.affiliate.earnings.sort_share_rate'),
                value: 'shareRate'
            }
        ];

        // Split to two lists via has data
        let earnings = [];
        let moreTitles = [];
        Object.keys(sectionData).forEach(key => {
            const item = sectionData[key];
            if (key === 'fortnite') {
                earnings.unshift(item);
            } else if (item.data && item.data.length) {
                earnings.push(item);
            } else {
                moreTitles.push(item);
            }
        });
        moreTitles = moreTitles.sort((a, b) => {
            // If they are equal, use secondary sort by name
            if (a[sortMoreTitles] === b[sortMoreTitles]) {
                return (sortAsc ? -1 : 1) * (b.name < a.name ? -1 : 1);
            }
            if (a[sortMoreTitles] < b[sortMoreTitles]) return sortAsc ? -1 : 1;
            if (a[sortMoreTitles] > b[sortMoreTitles]) return sortAsc ? 1 : -1;
            return 0;
        });

        // Filter earnings
        if (searchEarnings && searchEarnings.trim()) {
            const regex = new RegExp(searchEarnings.trim(), 'i');
            earnings = earnings.filter(item => regex.test(item.name));
        }

        // Filter more titles
        if (searchMoreTitles && searchMoreTitles.trim()) {
            const regex = new RegExp(searchMoreTitles.trim(), 'i');
            moreTitles = moreTitles.filter(item => regex.test(item.name));
        }

        const minEndDate = new Date(from);
        minEndDate.setDate(minEndDate.getDate() + 1);

        const maxEndDate = new Date(from);
        maxEndDate.setMonth(maxEndDate.getMonth() + 2);

        const now = new Date();

        const dayPickerProps = {
            modifiers: {
                disabled: [
                    {
                        before: minEndDate,
                        after: maxEndDate > now ? now : maxEndDate
                    }
                ]
            },
            modifiersStyles: {
                disabled: {
                    color: 'darkgray',
                    backgroundColor: ''
                }
            }
        };

        const tipContent = getMessage(
            messages,
            'epic.affiliate.earnings.my_earnings.tooltip.date_range.text'
        );

        return (
            <div className={className}>
                <div className={styles.listHead}>
                    <span onClick={this.toggleEarnings}>
                        <Message code="epic.affiliate.earnings.my_earnings.title" />
                        <Icon
                            className={
                                showEarnings
                                    ? styles.listIcon
                                    : classNames(styles.listIcon, styles.listOpen)
                            }
                            icon={svgs.CHEVRON_DOWN}
                            size={sizes.XSMALL}
                        />
                    </span>
                </div>
                {showEarnings && (
                    <div className={styles.listWrapper}>
                        <div className={styles.listTool}>
                            <Message code="epic.affiliate.earnings.date_range" />
                            <DayPickerInput
                                value={from}
                                format="YYYY-MM-DD"
                                onDayChange={this.onChange('from')}
                            />
                            <span>-</span>
                            <DayPickerInput
                                value={to}
                                format="YYYY-MM-DD"
                                onDayChange={this.onChange('to')}
                                dayPickerProps={dayPickerProps}
                            />
                            <Button
                                className={styles.listSubmit}
                                type={buttonTypes.BUTTON}
                                priority={priority.PRIMARY}
                                onClick={this.onSubmit}
                                hasMinWidth={true}
                                theme="dark">
                                <Message code="epic.affiliate.common.submit" />
                            </Button>

                            <Tip className={styles.tip} content={tipContent} position="bottom">
                                <Icon icon={svgs.INFO} size={sizes.XSMALL} />
                            </Tip>
                            <div className={styles.listSearch}>
                                <input
                                    type="text"
                                    name="searchEarnings"
                                    value={searchEarnings}
                                    onChange={this.onSearch}
                                />
                                <Icon icon={svgs.SEARCH} size={sizes.XSMALL} />
                            </div>
                        </div>
                        {earnings.map((item, index) => (
                            <Card
                                key={item.name}
                                name={item.name}
                                data={item}
                                time={time}
                                fill={COLORS[index % COLORS.length]}
                                startExpanded={index === 0}
                                enableLink={enableLink}
                                showLink={showLink}
                                messages={messages}
                            />
                        ))}
                        {!loading && earnings.length === 0 && (
                            <Message code="epic.affiliate.earnings.no_data" />
                        )}
                    </div>
                )}
                <div className={styles.listHead}>
                    <span onClick={this.toggleMoreTitles}>
                        <Message code="epic.affiliate.earnings.more_titles" />
                        <Icon
                            className={
                                showMoreTitles
                                    ? styles.listIcon
                                    : classNames(styles.listIcon, styles.listOpen)
                            }
                            icon={svgs.CHEVRON_DOWN}
                            size={sizes.XXSMALL}
                        />
                    </span>
                </div>
                {showMoreTitles && (
                    <div className={styles.listWrapper}>
                        <div className={styles.listTool}>
                            <Message code="epic.affiliate.earnings.sort_by" />
                            <DropList
                                className={styles.listSort}
                                listClass={styles.listSortPop}
                                name="sortMoreTitles"
                                value={sortMoreTitles}
                                searchable={false}
                                onChange={this.onSort}
                                options={sortOptions}
                            />
                            <div className={styles.listSearch}>
                                <input
                                    type="text"
                                    name="searchMoreTitles"
                                    value={searchMoreTitles}
                                    onChange={this.onSearch}
                                />
                                <Icon icon={svgs.SEARCH} size={sizes.XSMALL} />
                            </div>
                        </div>
                        {moreTitles.map((item, index) => (
                            <Card
                                key={item.name}
                                name={item.name}
                                data={item}
                                time={time}
                                fill={COLORS[index % COLORS.length]}
                                startExpanded={index === 0}
                                enableLink={enableLink}
                                showLink={showLink}
                                messages={messages}
                            />
                        ))}
                        {!loading && moreTitles.length === 0 && (
                            <Message code="epic.affiliate.earnings.no_data" />
                        )}
                    </div>
                )}
            </div>
        );
    }
}
